import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'normalize-css/normalize.css';
import 'styles/global/reset.css';
import 'styles/global/main.scss';
import { ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import ContextSnackbar from 'ui/modules/Snackbar/ContextSnackbar';
import { bluePlanetTheme } from 'ui/theme';
import React from 'react';
import Routes from './routes';
import Auth0ProviderWithHistory from 'auth/Auth0ProviderWithHistory';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { logError } from 'util/logging';
import { setupGcpErrorHandlers } from 'util/gcpLogging';
import { BrowserRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import CookieConsent from './CookieConsent';
import { KeyboardNavigationProvider } from 'contexts/KeyboardNavigationContext';
import { NotificationProvider } from 'contexts/NotificationContext';
import { IntercomProvider } from 'contexts/IntercomContext';
import { SessionExpiredProvider } from 'auth/SessionExpiredContext';
import { AccessTokenProvider } from 'auth/AccessTokenContext';
import SessionExpiredDialog from 'auth/SessionExpiredDialog';
import { hasTokenRefresher, refreshAccessToken, reportSessionExpired } from 'auth/sessionExpiredBridge';
import config from 'config';

// Set up global error handlers for capturing uncaught errors
setupGcpErrorHandlers();

// Our own API calls carry the token as a global axios default, so a 401 means the header went
// stale. Usually that's just the tab sleeping past the token's expiry while the Auth0 session is
// fine, so refresh and retry once first. Only when that doesn't help is the session really gone —
// report it so the user gets a way back in, and re-reject so no caller changes behaviour.
function isOwnApiCall(error: any) {
  const url: string = error?.config?.url ?? '';
  if (url.includes('/log-client-error')) return false;
  if (config.AUTH0_DOMAIN && url.includes(config.AUTH0_DOMAIN)) return false;
  return true;
}

axios.interceptors.response.use(
  response => response,
  async (error: any) => {
    const status = error.response?.status;

    if (status >= 500) {
      logError(error);
    } else if (status === 401 && isOwnApiCall(error)) {
      const request = error.config;
      if (!request || request._retriedAfterRefresh || !hasTokenRefresher()) {
        reportSessionExpired('api-401');
        return Promise.reject(error);
      }

      // A terminal refresh failure reports the expired session itself; a transient one stays quiet.
      const token = await refreshAccessToken().catch(() => undefined);
      if (token) {
        request._retriedAfterRefresh = true;
        request.headers.Authorization = `Bearer ${token}`;
        return axios(request);
      }
    }

    return Promise.reject(error);
  },
);

const container = document.getElementById('app');
const root = createRoot(container!);

function App() {
  return (
    <>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={bluePlanetTheme}>
          <NotificationProvider>
            <IntercomProvider>
              <ContextSnackbar />
              <BrowserRouter>
                <Auth0ProviderWithHistory>
                  <AccessTokenProvider>
                    <SessionExpiredProvider>
                      <KeyboardNavigationProvider>
                        <Routes />
                      </KeyboardNavigationProvider>
                      <SessionExpiredDialog />
                    </SessionExpiredProvider>
                  </AccessTokenProvider>
                </Auth0ProviderWithHistory>
              </BrowserRouter>
            </IntercomProvider>
          </NotificationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
      <CookieConsent />
    </>
  );
}

root.render(<App />);
